// The Internal catalogue: measures, products and formulas, the folders
// they are filed under, and the detail pane for each. Names, titles and specs
// are admin-authored and live in the database, so only the chrome is here.
import type { ProductImportField } from '../../constants/product-import'
import type { CatalogueKind, ProductClassificationKind } from '../../enums'

import { COMMON } from './common'
import { CONFIGURATOR } from './configurator'
import { CONFIGURATOR_CMS } from './configurator-cms'
import { CONTROLS } from './controls'
import { COST_LABELS, positionsInProjects } from './costs'
import {
  CATALOGUE_KIND_LABELS,
  CATALOGUE_KIND_SINGULAR_LABELS,
} from './enum-labels'
import { FIELD_LABELS } from './fields'
import { TERMS } from './terms'

/** The button the check callout points at; both read it, so a rename cannot
 *  leave the sentence naming a button that is gone. */
const IMPORT_CONFIRM = 'Produkt anlegen'

export const CATALOGUE = {
  /**
   * What an entry's mark falls back to while no icon is chosen. Shown in the
   * icon picker beside the button that clears one.
   *
   * Only the two an entry carries itself: where it has neither, the picker
   * states its own `CONTROLS.iconPicker.emptyFallback`.
   */
  iconFallback: {
    image: 'Ohne Icon wird das Kartenbild angezeigt.',
    color: 'Ohne Icon wird die Farbe angezeigt.',
  },
  removeImageMessage:
    'Das Bild wird von diesem Eintrag entfernt. In der Mediathek bleibt es erhalten.',

  /** The rail heading in every catalogue picker, and the tree's own word. */
  folders: 'Ordner',

  // ─── Tree ───────────────────────────────────────────────────────────
  newFolder: 'Neuer Ordner',
  newSubfolder: 'Unterordner anlegen',
  renameFolder: 'Ordner umbenennen',
  deleteFolder: 'Ordner löschen',
  deleteFolderConfirm: 'Möchtest du diesen Ordner wirklich löschen?',
  deleteConfirm: 'Möchtest du diesen Eintrag wirklich löschen?',
  /**
   * `folders` is how many of the rows are folders. A folder's tick carries
   * everything filed under it, and one click on a shut folder ticks rows the
   * operator never saw — so the count alone does not say what is going.
   */
  bulkDeleteConfirm: (rows: number, folders: number) => {
    const question =
      rows === 1
        ? 'Möchtest du diese Zeile wirklich löschen?'
        : `Möchtest du diese ${rows} Zeilen wirklich löschen?`
    if (folders === 0) return question
    return folders === 1
      ? `${question} Der ausgewählte Ordner wird mit seinem gesamten Inhalt gelöscht.`
      : `${question} Die ${folders} ausgewählten Ordner werden mit ihrem gesamten Inhalt gelöscht.`
  },
  // A selection holds folders as well as entries, and a folder is not an
  // entry — the row is the one word both are.
  selectedCount: (n: number) =>
    n === 1 ? '1 Zeile ausgewählt' : `${n} Zeilen ausgewählt`,
  /** On the marker that follows the pointer, where a single row shows its
   *  own name instead. */
  movingCount: (n: number) => (n === 1 ? '1 Zeile' : `${n} Zeilen`),

  bulkMoveBlocked: (n: number) =>
    n === 1
      ? 'Eine Zeile konnte nicht verschoben werden.'
      : `${n} Zeilen konnten nicht verschoben werden.`,

  topLevel: 'Oberste Ebene',
  moveToFolder: 'In Ordner verschieben …',
  deleteBlockedTitle: (n: number) =>
    n === 1
      ? 'Wird noch an einer Stelle verwendet:'
      : `Wird noch an ${n} Stellen verwendet:`,
  deleteBlockedMore: (n: number) => `… und ${n} weitere`,

  images: COMMON.images,
  /** The two roles an entry's pictures fall into. */
  cardImage: 'Kartenbild',
  cardImageHint:
    'Wird auf der Karte im Konfigurator und in jeder Liste gezeigt. Ohne Kartenbild bleibt dort ein Platzhalter.',
  productCardImageHint: `Wird als Farbfeld neben dem Namen und in jeder Liste gezeigt. Die Karte der ${TERMS.measure} im Konfigurator zeigt immer deren eigenes Kartenbild.`,
  previewImages: TERMS.previewImagePlural,
  previewImagesHint:
    'Werden im Konfigurator neben den Optionen durchgeblättert — erst die der Maßnahme, dann die der Produkte.',
  chooseCardImage: 'Kartenbild wählen',
  removeCardImageMessage:
    'Der Eintrag hat danach kein Kartenbild mehr und zeigt auf Karten und in Listen einen Platzhalter. In der Mediathek bleibt das Bild erhalten.',

  /** Pictures a measure shows only where a formula holds. The three modes are
   *  a Prisma enum and are labelled in `enum-labels`. */
  imageRules: {
    title: 'Bedingte Bilder',
    hint: `Bilder, die nur gezeigt werden, wenn ihre Formel wahr ergibt — ausgewertet an der Stelle, an der die ${TERMS.question} gestellt wird. Treffen mehrere zu, greifen sie von oben nach unten.`,
    empty: 'Keine bedingten Bilder',
    formula: 'Wenn',
    formulaHint:
      'Formel ergibt wahr → diese Bilder werden gezeigt. Leer heißt: trifft nie zu.',
    modeHint: `Die ${TERMS.previewImagePlural} der ${TERMS.productPlural} folgen in jedem Fall.`,
    cardImageHint: `Ersetzt das Kartenbild der ${TERMS.measure}, solange die Formel zutrifft. Ohne eins bleibt das normale Kartenbild stehen.`,
    removeMessage:
      'Die Regel und ihre Zuordnung werden gelöscht. Die Bilder selbst bleiben in der Mediathek erhalten.',
  },

  columns: {
    unit: FIELD_LABELS.unit.label,
    price: 'Preis',
    unitPrice: 'Einheitspreis',
    code: 'Code',
    manufacturer: 'Hersteller',
    functionName: CONFIGURATOR_CMS.formulas.functionName,
    inConfigurator: 'Im Konfigurator',
    inSummary: 'In der Auswertung',
    usages: TERMS.usagePlural,
  },

  /** What the toolbar narrows the tree by, beside the search box. */
  filters: {
    usage: TERMS.usage,
    used: 'In Verwendung',
    unused: TERMS.unused,
    noManufacturer: 'Ohne Hersteller',
  },

  /** The create action, named after what it creates. */
  addByKind: {
    MEASURE: `${CATALOGUE_KIND_SINGULAR_LABELS.MEASURE} anlegen`,
    PRODUCT: `${CATALOGUE_KIND_SINGULAR_LABELS.PRODUCT} anlegen`,
    LABOUR: `${CATALOGUE_KIND_SINGULAR_LABELS.LABOUR} anlegen`,
    FORMULA: `${CATALOGUE_KIND_SINGULAR_LABELS.FORMULA} anlegen`,
    PRICE_BLOCK: `${CATALOGUE_KIND_SINGULAR_LABELS.PRICE_BLOCK} anlegen`,
    PRICE_GROUP: `${CATALOGUE_KIND_SINGULAR_LABELS.PRICE_GROUP} anlegen`,
  } as const satisfies Record<CatalogueKind, string>,

  emptyByKind: {
    MEASURE: `Noch keine ${CATALOGUE_KIND_LABELS.MEASURE}`,
    PRODUCT: `Noch keine ${CATALOGUE_KIND_LABELS.PRODUCT}`,
    LABOUR: `Noch keine ${CATALOGUE_KIND_LABELS.LABOUR}`,
    FORMULA: `Noch keine ${CATALOGUE_KIND_LABELS.FORMULA}`,
    PRICE_BLOCK: `Noch keine ${CATALOGUE_KIND_LABELS.PRICE_BLOCK}`,
    PRICE_GROUP: `Noch keine ${CATALOGUE_KIND_LABELS.PRICE_GROUP}`,
  } as const satisfies Record<CatalogueKind, string>,

  /** What a picker's rail says over its first entry — the whole branch. */
  allOfKind: (kind: CatalogueKind) =>
    `${CONTROLS.filter.all} ${CATALOGUE_KIND_LABELS[kind]}`,

  /** The noun a picker's count is read out in. */
  countOfKind: (kind: CatalogueKind) => (n: number) =>
    n === 1
      ? CATALOGUE_KIND_SINGULAR_LABELS[kind]
      : CATALOGUE_KIND_LABELS[kind],

  /** What a picker's search box matches: what an entry is called, whatever
   *  identifies it, and — where the branch has folders — the folder it sits
   *  in, because the picker draws those and can match them. */
  searchByKind: {
    MEASURE: 'Maßnahme, Code, Ordner',
    PRODUCT: 'Produkt, Hersteller',
    LABOUR: 'Arbeitsleistung',
    FORMULA: 'Formel, Name, Ordner',
    PRICE_BLOCK: 'Preisbaustein, Name, Ordner',
    PRICE_GROUP: 'Preisgruppe, Name, Ordner',
  } as const satisfies Record<CatalogueKind, string>,

  /**
   * The four lists a product is filed under. Kept by an admin under
   * Verwaltung, picked per product — and the standard is the one a measure
   * wears too.
   */
  classification: {
    /** What each list is for, under its heading. */
    kindHints: {
      CATEGORY: 'Die gröbste Ebene — Bodenbeläge, Fenster, Türen.',
      GROUP: 'Worum es sich handelt — Parkett, Vinyl, Dachfenster.',
      DESIGN_LINE: 'Die Anmutung, quer durch alle Kategorien.',
      STANDARD:
        'Die Stufe, nach der der Konfigurator filtert. Auch an der Maßnahme wählbar.',
    } as const satisfies Record<ProductClassificationKind, string>,
    none: 'Nicht zugeordnet',
    empty: 'Noch nichts angelegt',
    nameTaken: (name: string) => `„${name}” steht in dieser Liste bereits.`,
    stillUsedError: (count: number) =>
      count === 1
        ? 'Dieser Eintrag ist noch einem Produkt zugewiesen.'
        : `Dieser Eintrag ist noch ${count} Produkten zugewiesen.`,
  },

  // ─── Measure detail ─────────────────────────────────────────────────
  measure: {
    /** Heads the sheet's first block — what the entry is, before what it does. */
    identity: 'Identität',
    texts: 'Texte',
    calculation: TERMS.calculation,
    /** The tab the availability rules are written on. */
    conditions: 'Bedingungen',
    /** The switches that decide where the measure may be offered at all. */
    offering: 'Angebot',
    addRule: 'Regel hinzufügen',
    addFormula: 'Formel hinzufügen',
    /** On the card the price expression is written on, and heading the dialog
     *  it opens. */
    priceFormula: 'Preisformel',
    priceFormulaHint: 'Ergebnis ist der Preis der Maßnahme in Euro.',
    /** The dropdown over how a measure is priced — the tree's filter bar and
     *  both catalogue pickers. */
    pricing: 'Preisart',
    availability: 'Verfügbarkeit',
    /** The measures a measure rules out, which rule it out in return. */
    exclusions: 'Schließt sich aus mit',
    exclusionsHint:
      'Wählt der Kunde eine der eingetragenen Maßnahmen, wird diese hier abgewählt — und umgekehrt. Geht die Abwahl auf seinen Klick zurück, bestätigt er sie vorher. Jeder Eintrag erscheint automatisch auch bei der Gegenseite.',
    noExclusions: 'Noch keine Maßnahme ausgeschlossen.',
    addExclusion: 'Maßnahme ausschließen',
    removeExclusion: 'Ausschluss aufheben',
    availabilityHint:
      'Diese Regeln gelten überall, wo die Maßnahme angeboten wird — im Konfigurator und bei der Positionsauswahl.',
    products: TERMS.productPlural,
    addProduct: `${TERMS.product} verknüpfen`,
    linkProducts: (n: number) =>
      `${n} ${n === 1 ? TERMS.product : TERMS.productPlural} verknüpfen`,
    noProducts: `Keine ${TERMS.productPlural} verknüpft`,
    shortDescription: TERMS.shortDescription,
    shortDescriptionHint:
      'Ein bis zwei Sätze. Steht auf der Konfigurator-Kachel und überall, wo die Maßnahme aufgelistet wird.',
    serviceDescription: TERMS.serviceDescription,
    serviceDescriptionHint:
      'Was ausgeführt wird, in den Worten des Angebots. Steht in beiden PDFs unter der Position.',

    /**
     * What rewording the measure does to the offers already given with the old
     * wording.
     *
     * A position freezes the text it was sold with, so a typo fixed here would
     * otherwise leave a Hinweis on every past position to be cleared by hand.
     * Only an administrator may rewrite what past offers say; everybody else
     * is told that those positions keep their wording.
     */
    textDrift: {
      title: `${TERMS.serviceDescription} ändern`,
      message: (positions: number, projects: number) =>
        `${positionsInProjects(positions, projects)} nennt noch den alten Text.`,
      /** The new wording, over the list of what still says the old one. */
      newText: 'Neuer Text',
      /** The administrator's two answers. */
      propagate: 'Text überall übernehmen',
      keep: 'Nur Maßnahme ändern',
      /** Said over the list, where the first answer is available. */
      consequence:
        'Diese Positionen nennen danach den neuen Text. Preis und Freigabe bleiben, wie sie sind.',
      /** Said there instead, to everybody else. */
      adminOnly:
        'Bestehende Positionen behalten ihren Text. Nur ein Administrator kann ihn dort ersetzen.',
    },
    detailedDescription: 'Ausführliche Beschreibung',
    detailedDescriptionHint:
      'Erscheint, wenn ein Kunde die Maßnahme aufklappt — nicht auf der Kachel. Bilder und Videos sind erlaubt.',
    // The badge the customer reads, so the switch that sets it says the same
    // word.
    fundingEligible: CONFIGURATOR.fundingEligible,
    fundingEligibleHint:
      'Blendet den Förderfähig-Hinweis bei der Maßnahme ein.',
    /** The ± rides in the label: the figure is read off it in both
     *  directions, and the row has no room for a sentence saying so. */
    priceRangePercent: 'Preisspanne ±',
    priceRangePercentHint:
      'Öffentliche Ansicht: statt eines festen Betrags wird eine Spanne von ± diesem Prozentsatz angezeigt.',
    availableAsPosition: 'Als Position auswählbar',
    availableAsPositionHint:
      'Bestimmt, ob die Maßnahme in der Planung als Position hinzugefügt werden kann.',
    /** Said instead of the switch where a question already places the measure:
     *  the switch could not decide anything there. */
    placedInConfigurator: `Wird über den Konfigurator gewählt — nicht als Position hinzufügbar.`,
    code: 'Code (Formel-Variable)',
    productGroupsHint:
      'Bestimmt die Eigenschaften der Maßnahme und welche Produkte für sie vorgeschlagen werden.',
    classHint:
      'Der Filter-Chip im Konfigurator — gilt überall, wo die Maßnahme angeboten wird.',
    codeHint:
      'Optional. Global eindeutiger Identifier in SCREAMING_SNAKE_CASE (z.B. MARMOR_FLIESEN). Nur Maßnahmen mit Code sind in Formeln referenzierbar (0/1).',
    disabledWhen: 'Deaktiviert wenn',
    disabledWhenHint:
      'Formel ergibt wahr → Maßnahme wird automatisch abgewählt und ist gesperrt.',
    hiddenWhen: 'Versteckt wenn',
    hiddenWhenHint: `Formel ergibt wahr → Maßnahme wird ausgeblendet und abgewählt. Ist dadurch die ganze ${TERMS.question} leer, entfällt sie.`,
    requiredWhen: 'Zwangsauswahl wenn',
    requiredWhenHint:
      'Formel ergibt wahr → Maßnahme wird automatisch gewählt und ist gesperrt.',
    deselectedWhen: 'Wird abgewählt wenn',
    deselectedWhenHint:
      'Formel ergibt wahr → Maßnahme wird abgewählt, bleibt aber wählbar. Wird die Formel durch einen Klick des Kunden wahr, bestätigt er die Abwahl vorher.',
    conflictResolution: 'Bei Konflikt',
  },

  // ─── Product detail ────────────────────────────────────────────────
  /** The Arbeitsleistungen branch: work charged at a rate. */
  labour: {
    rate: 'Satz',
    rateHint: `${TERMS.labourItemPlural} sind immer netto, je Einheit.`,
    activeHint: `Ausgeschaltet wird die ${TERMS.labourItem} nicht mehr angeboten. ${TERMS.measurePlural}, die sie bereits berechnen, behalten ihren Preis.`,
    inactive: 'Nicht mehr angeboten',
  },

  product: {
    /** Master data, procurement and source on one page. */
    dataSheet: 'Datenblatt',
    masterData: TERMS.masterData,
    classification: 'Klassifikation',
    collectionPlaceholder: 'z. B. Creation 55',
    /** Marks a row of the catalogue whose product is switched off. */
    inactive: 'Nicht mehr geführt',
    activeHint:
      'Ausgeschaltet wird das Produkt nicht mehr angeboten. Projekte, die es bereits führen, behalten es.',
    /** Beside the price, because nothing else on the screen says which it is. */
    netPriceHint: 'Produktpreise sind immer netto.',
    procurement: 'Beschaffung',
    articleNumber: 'Artikelnummer',
    supplier: 'Lieferant',
    purchasePrice: 'Einkaufspreis',
    deliveryTime: 'Lieferzeit',
    color: 'Farbwert',
    colorPlaceholder: '#C9A227',
    colorHint:
      'Ohne Bild wird der Farbwert als Farbfeld angezeigt. Mit Bild gewinnt das Bild.',
    source: TERMS.source,
    draft: 'Entwurf',
    draftTitle: 'Noch nicht freigegeben',
    draftHint:
      'Der Import hat die Seite nicht vollständig lesen können. Solange das Produkt ein Entwurf ist, wird es im Konfigurator nicht angeboten.',
    publish: 'Freigeben',
    importFromLink: 'Aus Link anlegen',
    importUrl: 'Produkt-Link',
    importUrlPlaceholder: 'https://…',
    importRunning: 'Seite wird gelesen und ausgewertet …',
    importGapsHint:
      'Diese Felder hat die Produktseite nicht genannt. Trage sie nach und der Hinweis verschwindet.',
    importedFrom: 'Übernommen von',
    importUnreachable: 'Die Seite ist nicht erreichbar.',
    importDraftMalformed:
      'Der Entwurf ist unvollständig. Lies die Seite noch einmal ein.',
    importTooLarge: 'Die Seite ist zu groß, um sie zu lesen.',
    importReadFailed: (reason: string) =>
      reason === 'blocked'
        ? 'Die Seite hat die Anfrage abgelehnt. Lege das Produkt von Hand an.'
        : reason === 'not-html'
          ? 'Der Link zeigt nicht auf eine Produktseite.'
          : reason === 'too-large'
            ? CATALOGUE.product.importTooLarge
            : CATALOGUE.product.importUnreachable,
    importExtractionFailed:
      'Die Seite konnte nicht ausgewertet werden. Prüfe die AI-Einstellungen im Systembereich.',
    importNoSourceUrl: 'Dieses Produkt stammt nicht aus einem Link.',
    /** Over the form the operator confirms, so it is clear nothing is saved
     *  yet and that the figures are worth a look. */
    importCheck: `Prüfe, was die Seite hergegeben hat. Angelegt wird erst mit „${IMPORT_CONFIRM}”.`,
    importConfirm: IMPORT_CONFIRM,
    /** Said where the page quoted a consumer price, because the field under
     *  it then holds a different number than the shop shows. */
    importQuotedGross: (gross: string, rate: number) =>
      `Seitenpreis ${gross} ${COST_LABELS.vatIncluded(rate)}, eingetragen als Netto-Betrag.`,
    /** Where the page says nothing about VAT. The figure is read as gross —
     *  what a shop without a trade login quotes — and that is a reading, not
     *  something the page stated. */
    importAssumedGross: (gross: string, rate: number) =>
      `Die Seite sagt nichts zur MwSt. — ${gross} als Brutto gelesen und mit ${rate} % netto eingetragen.`,
    /** Which of the two figures on the page the catalogue took. The list price
     *  is what it wants; the asking price is what is left when there is none. */
    importListPrice:
      'Übernommen ist der UVP der Seite, nicht der Angebotspreis.',
    importNoListPrice:
      'Die Seite nennt keinen UVP — übernommen ist der aktuell geforderte Preis. Bitte prüfen.',
    importFound: (properties: number, images: number) =>
      `${properties} ${properties === 1 ? 'technische Angabe' : 'technische Angaben'}, ${images} ${images === 1 ? 'Bild' : 'Bilder'}.`,
    priceUnchanged: 'Der Preis auf der Seite ist unverändert.',
    priceChanged: (from: number | null, to: number | null) =>
      `Preis auf der Seite: ${to ?? '—'} € (im Katalog: ${from ?? '—'} €)`,
    priceNotOnPage: 'Die Seite nennt keinen Preis mehr.',
    recheckPrice: 'Preis erneut prüfen',
  },

  // ─── Formula detail ─────────────────────────────────────────────────
  /**
   * The tab every branch shows: what uses this entry.
   *
   * One pair, because the six panes draw one list through one component and an
   * operator moving between them read the same fact four ways — "Verwendet
   * in", "Berechnet von", "Verwendet von Maßnahmen". The heading names no kind
   * of user on purpose: what charges an Arbeitsleistung is a Preisgruppe as
   * readily as a Maßnahme.
   */
  usage: {
    heading: COMMON.usedIn,
    empty: 'Wird nirgends verwendet',
    /** Under a Preisgruppe's row: how far it carries the entry. */
    reaches: (count: number) =>
      count === 1
        ? `Über diese ${TERMS.priceGroup} in 1 ${TERMS.measure}`
        : `Über diese ${TERMS.priceGroup} in ${count} ${TERMS.measurePlural}`,
  },

  formula: {
    references: 'Referenzen',
    usedBy: 'Wird verwendet von',
    usedByHint: `${TERMS.measurePlural} und ${TERMS.formulaPlural}, die diese Formel aufrufen`,
    reads: 'Liest selbst',
    readsHint: `${TERMS.formulaPlural} und Werte, die diese Formel einliest`,
    readsNothing: 'Liest keine anderen Werte',
  },

  // ─── Deleting something in use ──────────────────────────────────────

  errors: {
    kindUnknown: 'Diesen Zweig des Katalogs gibt es nicht.',
    excludesItself: 'Eine Maßnahme kann sich nicht selbst ausschließen.',
    exclusionNotFound:
      'Eine der ausgewählten Maßnahmen gibt es nicht mehr. Lade die Seite neu.',
    // A measure code and a formula name share one namespace: whichever is
    // read second silently shadows the other's 0/1 flag.
    codeCollidesWithFormula: (code: string) =>
      `Der Maßnahmen-Code „${code}“ ist schon als Formelname vergeben. Benenne eines von beiden um.`,
    formulaNameCollidesWithCode: (name: string) =>
      `Der Formelname „${name}“ ist schon als Maßnahmen-Code vergeben. Benenne eines von beiden um.`,
    codeTaken: (code: string) =>
      `Der Maßnahmen-Code „${code}“ ist bereits vergeben.`,
    codeStillReferenced: (
      code: string,
      measureIds: number[],
      formulaNames: string[],
      blockIds: number[] = [],
    ) => {
      const parts = [
        measureIds.length === 1
          ? `1 ${TERMS.measure}`
          : measureIds.length > 1
            ? `${measureIds.length} ${TERMS.measurePlural}`
            : null,
        formulaNames.length === 1
          ? `1 ${TERMS.formula}`
          : formulaNames.length > 1
            ? `${formulaNames.length} ${TERMS.formulaPlural}`
            : null,
        blockIds.length === 1
          ? `1 ${TERMS.block}`
          : blockIds.length > 1
            ? `${blockIds.length} ${TERMS.blockPlural}`
            : null,
      ].filter(Boolean)
      return `Der Code „${code}“ wird noch verwendet von ${parts.join(', ')}. Entferne diese Referenzen zuerst.`
    },
    measureInConfigurator: `Diese ${TERMS.measure} steht im Konfigurator. Sie kommt über die Auswahl des Kunden auf die Planung, nicht als Position.`,
    measureNotAvailableAsPosition: `Diese ${TERMS.measure} ist nicht als Position auswählbar.`,
    measureRuleBlocksPosition: (title: string) =>
      `Die Regeln von „${title}“ treffen für dieses Objekt nicht zu.`,
    calculationAdminOnly: 'Die Kalkulation ändern nur Administratoren.',
    measureStillPlaced: (count: number) =>
      count === 1
        ? `Diese ${TERMS.measure} ist noch in einer ${TERMS.question} platziert.`
        : `Diese ${TERMS.measure} ist noch in ${count} Sections platziert.`,
    productStillCharged: (count: number) =>
      count === 1
        ? `Dieses ${TERMS.product} wird noch von einer Preiszeile berechnet. Nimm sie zuerst aus dem Preis.`
        : `Dieses ${TERMS.product} wird noch von ${count} Preiszeilen berechnet. Nimm sie zuerst aus dem Preis.`,
    productStillLinked: (count: number) =>
      count === 1
        ? `Dieses ${TERMS.product} ist noch mit einer ${TERMS.measure} verknüpft.`
        : `Dieses ${TERMS.product} ist noch mit ${count} ${TERMS.measurePlural} verknüpft.`,
    folderNotEmpty: (count: number) =>
      count === 1
        ? 'Der Ordner enthält noch einen Eintrag.'
        : `Der Ordner enthält noch ${count} Einträge.`,
    formulaNameTaken: (name: string) =>
      `Der Funktionsname „${name}” ist bereits vergeben.`,
    folderNameTaken: (name: string) =>
      `Auf dieser Ebene gibt es bereits einen Ordner „${name}”.`,
    nameRequired: 'Ohne Bezeichnung lässt sich das Produkt nicht anlegen.',
    classificationKindMismatch: 'Dieser Eintrag gehört zu einer anderen Liste.',
    kindHasNoFolders:
      'Produkte werden über ihre Klassifikation gefunden, nicht über Ordner.',
    folderKindMismatch: 'Ordner können nur Einträge derselben Art enthalten.',
  },
} as const

/**
 * The import badges, named as the detail pane names the same inputs. Defined
 * after `CATALOGUE` because an object literal cannot read itself.
 */
export const PRODUCT_IMPORT_FIELD_LABELS = {
  name: COMMON.designation,
  description: FIELD_LABELS.description.label,
  manufacturer: CATALOGUE.columns.manufacturer,
  articleNumber: CATALOGUE.product.articleNumber,
  supplier: CATALOGUE.product.supplier,
  unitPrice: CATALOGUE.columns.unitPrice,
  unit: CATALOGUE.columns.unit,
  hex: CATALOGUE.product.color,
  deliveryTime: CATALOGUE.product.deliveryTime,
} as const satisfies Record<ProductImportField, string>
